import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { Log, IShortLogger } from './logger.service';

/**
 * Interface defining the structure of VM launch session data.
 * Contains all necessary information to launch and authenticate a VM session.
 *
 * @interface LaunchSessionData
 */
export interface LaunchSessionData {
  /** Server-generated encrypted token containing all VM connection details */
  encryptedToken: string;

  /** Cloud username for VM authentication (displayed in sidebar) */
  cloudUserName: string;

  /** Cloud password for VM authentication (displayed in sidebar) */
  password: string;

  /** Optional class identifier for tracking purposes */
  classId?: string;

  /** Optional user identifier for tracking purposes */
  userId?: string;
}

/**
 * Service for managing VM launch session data in localStorage.
 *
 * This service handles storing and retrieving encrypted VM connection tokens
 * and credentials across browser tabs/windows. It uses a simple override approach
 * where new launches for the same class-id replace existing data.
 *
 * Data is stored in localStorage under the key 'current_launched_sessions' with
 * the structure: { 'class-id': LaunchSessionData }
 *
 * @class LaunchSessionStoreService
 *
 * @example
 * ```typescript
 * // Inject in component
 * constructor(private launchStore: LaunchSessionStoreService) {}
 *
 * // Store launch data when launching VM
 * this.launchStore.storeLaunchSession('class-123', {
 *   encryptedToken: 'server-encrypted-token',
 *   cloudUserName: 'azureuser',
 *   password: 'P@ssw0rd123',
 *   classId: 'class-123',
 *   userId: 'user-456'
 * });
 *
 * // Retrieve launch data in VM window
 * const launchData = this.launchStore.getLaunchSession('class-123');
 * if (launchData) {
 *   // Use data to initialize VM connection
 * }
 *
 * // Clean up when done
 * this.launchStore.removeLaunchSession('class-123');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class LaunchSessionStoreService {
  private readonly logger: IShortLogger<LaunchSessionStoreService> = Log.create(LaunchSessionStoreService);

  /**
   * The localStorage key used to store all launch sessions.
   * @private
   * @readonly
   * @type {string}
   */
  private readonly STORAGE_KEY: string = 'current_launched_sessions';

  /**
   * Creates an instance of LaunchSessionStoreService.
   *
   * @param {LocalStorageService} localStorageService - Service for localStorage operations
   */
  constructor(private localStorageService: LocalStorageService) { }

  /**
   * Store launch session data for a specific class.
   * If data already exists for the class-id, it will be overwritten.
   *
   * @param {string} classId - The unique class identifier
   * @param {LaunchSessionData} launchData - The session data to store
   *
   * @example
   * ```typescript
   * this.launchStore.storeLaunchSession('class-123', {
   *   encryptedToken: 'encrypted-token-here',
   *   cloudUserName: 'azureuser',
   *   password: 'password123'
   * });
   * ```
   */
  storeLaunchSession(classId: string, launchData: LaunchSessionData): void {
    const currentSessions = this.getAllLaunchSessions();

    // Simple override - no cleanup needed as per requirement
    currentSessions[classId] = {
      ...launchData,
      classId
    };

    this.localStorageService.setItem(this.STORAGE_KEY, currentSessions);
    this.logger.d(`Stored launch session for class: ${classId}`, launchData);
  }

  /**
   * Retrieve launch session data for a specific class.
   *
   * @param {string} classId - The unique class identifier
   * @returns {LaunchSessionData | null} The session data or null if not found
   *
   * @example
   * ```typescript
   * const sessionData = this.launchStore.getLaunchSession('class-123');
   * if (sessionData) {
   *   this.logger.d('Token:', sessionData.encryptedToken);
   *   this.logger.d('Username:', sessionData.cloudUserName);
   * }
   * ```
   */
  getLaunchSession(classId: string): LaunchSessionData | null {
    const currentSessions = this.getAllLaunchSessions();
    const sessionData = currentSessions[classId];

    if (sessionData) {
      this.logger.d(`Retrieved launch session for class: ${classId}`, sessionData);
      return sessionData;
    } else {
      this.logger.w(`No launch session found for class: ${classId}`);
      return null;
    }
  }

  /**
   * Remove launch session data for a specific class.
   *
   * @param {string} classId - The unique class identifier
   *
   * @example
   * ```typescript
   * this.launchStore.removeLaunchSession('class-123');
   * ```
   */
  removeLaunchSession(classId: string): void {
    const currentSessions = this.getAllLaunchSessions();
    delete currentSessions[classId];

    this.localStorageService.setItem(this.STORAGE_KEY, currentSessions);
    this.logger.d(`Removed launch session for class: ${classId}`);
  }

  /**
   * Get all current launch sessions.
   *
   * @returns {{ [classId: string]: LaunchSessionData }} Object containing all launch sessions keyed by class-id
   *
   * @example
   * ```typescript
   * const allSessions = this.launchStore.getAllLaunchSessions();
   * this.logger.d('Active sessions:', Object.keys(allSessions).length);
   * ```
   */
  getAllLaunchSessions(): { [classId: string]: LaunchSessionData } {
    return this.localStorageService.getItem<{ [classId: string]: LaunchSessionData }>(this.STORAGE_KEY) || {};
  }

  /**
   * Clear all launch sessions from storage.
   * This removes all stored VM launch data for all classes.
   *
   * @example
   * ```typescript
   * this.launchStore.clearAllLaunchSessions();
   * ```
   */
  clearAllLaunchSessions(): void {
    this.localStorageService.removeItem(this.STORAGE_KEY);
    this.logger.i('Cleared all launch sessions');
  }
}
